import { send } from '../../api/modules/apiV2';
import type {
  RewardsProfile,
  CatalogueSku,
  SkuDetail,
  ReserveResult,
  RedeemResult,
  RewardClaim,
  RestockScheduleEntry,
  ActivityResponse,
} from './types';
import type { IRewardsService } from './IRewardsService';

const STORE_PREFIX = 'realtime/rewards/store';
const PROFILE_PREFIX = 'realtime/rewards';

/**
 * One rung of the ladder above the baseline. Display fields only — the next
 * tier's component checks come from the machine's stored detail, not from here.
 */
export interface TierLadderEntry {
  tierId: string;
  name: string;
  priority: number;
  earnRateDescription?: string;
  maxEarningsDescription?: string;
  detailSummary?: string;
}

export class RewardsService implements IRewardsService {
  async getProfile(): Promise<RewardsProfile> {
    return send('get', `${PROFILE_PREFIX}/profile`);
  }

  async getCatalogue(): Promise<CatalogueSku[]> {
    return send('get', `${STORE_PREFIX}/catalogue`);
  }

  async getSkuDetail(skuId: string): Promise<SkuDetail> {
    return send('get', `${STORE_PREFIX}/sku/${skuId}`);
  }

  async reserve(skuId: string, clientRequestId: string): Promise<ReserveResult> {
    return send('post', `${STORE_PREFIX}/reserve`, { skuId, clientRequestId });
  }

  async confirm(claimId: string): Promise<RedeemResult> {
    return send('post', `${STORE_PREFIX}/confirm`, { claimId });
  }

  async cancel(claimId: string): Promise<void> {
    return send('post', `${STORE_PREFIX}/cancel`, { claimId });
  }

  async getClaims(): Promise<RewardClaim[]> {
    const res = await send('get', `${STORE_PREFIX}/claims?limit=100`);
    if (res && 'success' in res && !res.success) return [];
    return Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : [];
  }

  async getRestockSchedule(): Promise<{ schedule: RestockScheduleEntry[] }> {
    return send('get', `${STORE_PREFIX}/restock-schedule`);
  }

  async getActivity(limit = 50, offset = 0): Promise<ActivityResponse> {
    return send('get', `${PROFILE_PREFIX}/activity?limit=${limit}&offset=${offset}`);
  }

  async getHostMessages(): Promise<{ text: string; tiers?: string[] }[]> {
    const res = await send('get', `${PROFILE_PREFIX}/host-messages`);
    const raw = Array.isArray(res?.messages) ? res.messages : [];
    return raw.map((m: unknown) =>
      typeof m === 'string' ? { text: m } : (m as { text: string; tiers?: string[] }),
    );
  }

  async getRebootIncentive(): Promise<{ amount: number } | null> {
    const res = await send('get', `${PROFILE_PREFIX}/reboot-incentive`);
    return res?.incentive ?? null;
  }

  /**
   * The enabled hardware tiers above the baseline, for the ladder.
   *
   * Display fields only — the baseline rules and capabilities are configuration
   * and are not exposed here. The next tier's component CHECKS are not here
   * either: those come from the machine's stored detail, since only the next
   * tier carries that level of detail.
   */
  async getTierLadder(): Promise<TierLadderEntry[]> {
    const res = await send('get', `${PROFILE_PREFIX}/tiers`);
    return Array.isArray(res?.tiers) ? (res.tiers as TierLadderEntry[]) : [];
  }

  async fireAction(actionType: string, machineId: string, payload: Record<string, unknown>): Promise<unknown> {
    return send('post', `${PROFILE_PREFIX}/actions/fire`, { actionType, machineId, payload });
  }
}
